import { Activity } from "../generated/activity-model";
import { CreateInvestmentInput } from "../generated/create-investment-input";
import { Sale } from "../generated/sale-model";
import { Skill } from "../generated/skill-model";
import { UpdateUserInput } from "../generated/update-user-input";
import { ActivityType, AverageYearlyTransactionsType, AverageYearlyVolumeType, ExperienceLevel, InvestmentClass, XpectoInvestmentType } from "./utils";

export const investmentClassToXpectoMapping = {
  [InvestmentClass.BOND]: XpectoInvestmentType.BOND,
  [InvestmentClass.CRYPTO_CURRENCY]: XpectoInvestmentType.CRYPTO_CURRENCY,
  [InvestmentClass.INVESTMENT_FUND]: XpectoInvestmentType.INVESTMENT_FUND,
  [InvestmentClass.PARTICIPATION_RIGHT]: XpectoInvestmentType.PARTICIPATION_RIGHT,
  [InvestmentClass.PRIVATE_EQUITY]: XpectoInvestmentType.PRIVATE_EQUITY,
  [InvestmentClass.SHARE]: XpectoInvestmentType.SHARE,
  [InvestmentClass.UNSECURED_JUNIOR_DEBT]: XpectoInvestmentType.UNSECURED_JUNIOR_DEBT,
}

export const xpectoToInvestmentClassMapping = Object.entries(investmentClassToXpectoMapping).reduce(
  (
    currentState, [key, value]
  ) => ({
    ...currentState,
    [value]: key,
  }),
  { }
);

// Utility methods -

const calculateActivityScore = (activity: Activity) : number => {
  let score = 0;

  // Experience level
  switch (activity.type) {
    case ActivityType.WP_SERVICE:
      switch (activity.levelWithoutAdvice) {
        case ExperienceLevel.LOW:
          score += 4;
          break;
        case ExperienceLevel.MEDIUM:
          score += 4;
          break;
        case ExperienceLevel.HIGH:
          score += 4;
          break;
        default:
          break;
      }

      switch (activity.levelWithAdvice) {
        case ExperienceLevel.LOW:
          score += 2;
          break;
        case ExperienceLevel.MEDIUM:
          score += 2;
          break;
        case ExperienceLevel.HIGH:
          score += 2;
          break;
        default:
          break;
      }

      switch (activity.levelWithAssetManagement) {
        case ExperienceLevel.LOW:
          score += 1;
          break;
        case ExperienceLevel.MEDIUM:
          score += 1;
          break;
        case ExperienceLevel.HIGH:
          score += 1;
          break;
        default:
          break;
      }
      break;
    default:
      break;
  }

  return score;
}

const calculateActivityQuantityScore = (activity: Activity) : number => {
  // Experience in number of transactions
  let transactionScore = 0;
  if (activity.transactionsPerYear) {
    const transactionsPerYearMapping = {
      [AverageYearlyTransactionsType.HIGH]: 100,
      [AverageYearlyTransactionsType.MEDIUM]: 10,
      [AverageYearlyTransactionsType.LOW]: 5,
      [AverageYearlyTransactionsType.NONE]: 0,
    };
    transactionScore = transactionsPerYearMapping[activity.transactionsPerYear];
  }

  // Experience in volume
  let volumeScore = 0;
  if (activity.volumePerYear) {
    const volumePerYearMapping = {
      [AverageYearlyVolumeType.VERY_HIGH]: 100000,
      [AverageYearlyVolumeType.HIGH]: 10000,
      [AverageYearlyVolumeType.MEDIUM]: 5000,
      [AverageYearlyVolumeType.LOW]: 3000,
    };
    volumeScore = volumePerYearMapping[activity.volumePerYear];
  }

  return transactionScore * volumeScore / 2;
}

export const maxScoreInvestmentClass = (investmentClass: InvestmentClass) : number => {
  return calculateSkillScore({
    type: investmentClass,
    level: ExperienceLevel.HIGH,
    hasKnowledge: true,
  });
}

const calculateSkillScore = (skill: Skill) : number => {
  let score = 0;

  // Knowledge
  score += skill.hasKnowledge ? 1 : 0;

  // Experience level
  switch (investmentClassToXpectoMapping[skill.type]) {
    // Basic
    case XpectoInvestmentType.INVESTMENT_FUND:
    case XpectoInvestmentType.GOVERNMENT_BONDS:
    case XpectoInvestmentType.MONEY_MARKET_FUNDS:
      switch (skill.level) {
        case ExperienceLevel.LOW:
          score += 1;
          break;
        case ExperienceLevel.MEDIUM:
          score += 2;
          break;
        case ExperienceLevel.HIGH:
          score += 2;
          break;
        default:
          break;
      }
      break;
    // Advanced
    case XpectoInvestmentType.BOND:
    case XpectoInvestmentType.SHARE:
    case XpectoInvestmentType.CERTIFICATE:
    case XpectoInvestmentType.REVERSE_CONVERTIBLE_BOND:
      switch (skill.level) {
        case ExperienceLevel.LOW:
          score += 2;
          break;
        case ExperienceLevel.MEDIUM:
          score += 3;
          break;
        case ExperienceLevel.HIGH:
          score += 3;
          break;
        default:
          break;
      }
      break;
    // Extensive
    case XpectoInvestmentType.ALTERNATIVE_INVESTMENT_FUND:
    case XpectoInvestmentType.WARRANTS:
    case XpectoInvestmentType.EMERGING_MARKET_BONDS:
      switch (skill.level) {
        case ExperienceLevel.LOW:
          score += 3;
          break;
        case ExperienceLevel.MEDIUM:
          score += 4;
          break;
        case ExperienceLevel.HIGH:
          score += 4;
          break;
        default:
          break;
      }
      break;
    // Special
    case XpectoInvestmentType.HEDGE_FUND:
    case XpectoInvestmentType.FUTURES_TRADING:
    case XpectoInvestmentType.PARTICIPATION_PAPER:
    case XpectoInvestmentType.PARTICIPATION_RIGHT:
    case XpectoInvestmentType.OPERATIVE_PARTICIPATION_IN_A_LIMITED_PARTNERSHIP:
    case XpectoInvestmentType.PARTICIPATION_IN_A_LIMITED_PARTNERSHIP:
    case XpectoInvestmentType.CONTRACT_FOR_DIFFERENCE:
    case XpectoInvestmentType.CRYPTO_CURRENCY:
    case XpectoInvestmentType.FIXED_DEPOSIT:
    case XpectoInvestmentType.PRIVATE_EQUITY:
    case XpectoInvestmentType.UNSECURED_JUNIOR_DEBT:
      switch (skill.level) {
        case ExperienceLevel.LOW:
          score += 4;
          break;
        case ExperienceLevel.MEDIUM:
          score += 5;
          break;
        case ExperienceLevel.HIGH:
          score += 5;
          break;
        default:
          break;
      }
      break;
    default:
      break;
  }

  return score;
}

const calculateArbitraryScore = (
  data: Array<Skill | Activity>, scoreType: InvestmentClass | ActivityType, calculateScore: (data: any) => number
) : number => {
  const xpData = data.find((xp: Skill | Activity) => scoreType === xp.type);
  return xpData ? calculateScore(xpData) : 0;
}

// Effecta score calculation

const calculateCustomerGroupScore = (user: UpdateUserInput) : number => {
  return user.company ? 2 : 1;
}

const calculateInvestmentClassScore = (
  user: UpdateUserInput, investmentClass: InvestmentClass
) : number => {
  return calculateArbitraryScore(
    user.experience?.skills || [],
    investmentClass,
    calculateSkillScore
  );
}

const calculateWPServicesScore = (user: UpdateUserInput) : number => {
  return calculateArbitraryScore(
    user.experience?.activities || [],
    ActivityType.WP_SERVICE,
    calculateActivityScore
  );
}

const calculateTransactionsAndVolumeScore = (user: UpdateUserInput) : number => {
  return calculateArbitraryScore(
    user.experience?.activities || [],
    ActivityType.WP_SERVICE,
    calculateActivityQuantityScore
  );
}

export const calculateUserTotalScore = (user: UpdateUserInput) : number => {
  let score = 0;

  if (user.experience) {
    user.experience.skills?.forEach((xp: Skill) => score += calculateSkillScore(xp));
    user.experience.activities?.forEach((xp: Activity) => score += calculateActivityScore(xp) + calculateActivityQuantityScore(xp));
  }

  return score;
}

export const isUserFitForSale = (
  user: UpdateUserInput, sale: Sale, investment: CreateInvestmentInput
) : boolean => {

  // Customer group eligibility
  if (calculateCustomerGroupScore(user) < sale.scoreCustomerGroup) {
    return false;
  }

  // Knowledge & Experience
  const userInvestmentClassScore = Object.values(InvestmentClass).reduce(
    (
      result: number, investmentClass: InvestmentClass
    ) => result + calculateInvestmentClassScore(
      user,
      investmentClass
    ),
    0
  );

  if (userInvestmentClassScore < sale.scoreInvestmentClass) {
    return false;
  }

  // WP Services
  if (calculateWPServicesScore(user) < sale.scoreWPServices) {
    return false;
  }

  // WP Services
  if (calculateTransactionsAndVolumeScore(user) < investment.amountShares) {
    return false;
  }

  return true;
}